import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useNotificationStore } from '@/stores/notification'
import { useLoaderStore } from '@/stores/loader'

const router = createRouter({
  linkActiveClass: 'border-green-600',
  linkExactActiveClass: 'border-green-600',
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'main',
      component: () => import('@/views/MainView.vue'),
      // redirect: { name: 'login' },
      meta: { user: 'public' }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { user: 'public' }
    },

    /**
     *  Registration routings
     */
    {
      path: '/registration',
      name: 'registration',
      component: () => import('@/views/RegistrationView.vue'),
      meta: { user: 'public' },
      children: [
        {
          path: '',
          name: 'registration-school',
          component: () => import('@/views/registration/SchoolRegisterView.vue')
        }
      ]
    },

    /**
     * Public actions (potwierdzenie e-maila, reset hasła) — dzieci celowo używają
     * ścieżek bezwzględnych (z wiodącym "/"), dzięki czemu ignorują prefiks
     * rodzica i nie pojawia się on w URL. To pozwala trzymać krótkie, płaskie
     * adresy (np. /confirm-email/:token) wysyłane w mailach, przy zachowaniu
     * wspólnego layoutu (PublicView) — w przeciwieństwie do /registration
     * powyżej, gdzie prefiks w URL jest pożądany. PublicView i RegistrationView
     * to celowo osobne pliki (dziś identyczne) — rejestracja i akcje publiczne
     * nie są biznesowo powiązane i mogą w przyszłości rozjechać się layoutem.
     */

    {
      path: '/public-actions',
      name: 'public-actions',
      component: () => import('@/views/PublicView.vue'),
      meta: { user: 'public' },
      children: [
        {
          path: '',
          name: 'public-actions-redirect',
          redirect: { name: 'not-found' }
        },
        {
          // forceLogout: link z maila bywa klikany przy cudzej aktywnej sesji —
          // szkoła rejestruje ucznia w jego obecności, uczeń otwiera swoją
          // skrzynkę na tym samym urządzeniu. Przy zmianie adresu jest to
          // wręcz typowe, bo stary login działa aż do potwierdzenia nowego.
          path: '/confirm-email/:token',
          name: 'confirm-email',
          meta: { forceLogout: true },
          component: () => import('@/views/shared/ConfirmEmailView.vue')
        },
        {
          path: '/password-reset/request',
          name: 'password-reset-request',
          component: () => import('@/views/shared/PasswordResetRequestView.vue')
        },
        {
          path: '/password-reset/execute/:token',
          name: 'password-reset-execute',
          component: () => import('@/views/shared/PasswordResetExecuteView.vue')
        }
      ]
    },

    /**
     * Admin routings
     */
    {
      path: '/admin',
      name: 'admin',
      component: () => import('@/views/AdminView.vue'),
      meta: { user: 'admin' },
      children: [
        {
          path: '',
          name: 'admin-home',
          component: () => import('@/views/admin/AdminHomeView.vue'),
          meta: { menuKey: 'admin-home' }
        },
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('@/views/admin/AdminUsersView.vue'),
          meta: { menuKey: 'admin-users' }
        },
        {
          path: 'schools',
          name: 'admin-schools',
          component: () => import('@/views/admin/AdminSchoolsView.vue'),
          meta: { menuKey: 'admin-schools' },
          children: [
            {
              path: '',
              name: 'admin-schools-list',
              component: () => import('@/views/admin/admin-schools/AdminSchoolsListView.vue')
            }
          ]
        },
        {
          path: 'students',
          name: 'admin-students',
          component: () => import('@/views/admin/AdminStudentsView.vue'),
          meta: { menuKey: 'admin-students' },
          children: [
            {
              path: '',
              name: 'admin-students-list',
              component: () => import('@/views/admin/admin-students/AdminStudentsListView.vue')
            }
          ]
        }
      ]
    },

    /**
     * KGOB routings
     */
    {
      path: '/kgob',
      name: 'kgob',
      component: () => import('@/views/KgobView.vue'),
      meta: { user: 'kgob' },
      children: [
        {
          path: '',
          name: 'kgob-home',
          component: () => import('@/views/kgob/KgobHomeView.vue'),
          meta: { menuKey: 'kgob-home' }
        },
        {
          path: 'students',
          name: 'kgob-students',
          component: () => import('@/views/kgob/KgobStudentsView.vue'),
          meta: { menuKey: 'kgob-students' },
          children: [
            {
              path: '',
              name: 'kgob-students-list',
              component: () => import('@/views/kgob/kgob-students/KgobStudentsListView.vue')
            },
            {
              path: 'edit/:id',
              name: 'kgob-students-edit',
              component: () => import('@/views/kgob/kgob-students/KgobStudentsEditView.vue')
            },
            {
              path: ':id/edit-review',
              name: 'kgob-students-edit-review',
              component: () => import('@/views/kgob/kgob-review/KgobReviewEditView.vue')
            }
          ]
        },
        {
          path: 'schools',
          name: 'kgob-schools',
          component: () => import('@/views/kgob/KgobSchoolsView.vue'),
          meta: { menuKey: 'kgob-schools' },
          children: [
            {
              path: '',
              name: 'kgob-schools-list',
              component: () => import('@/views/kgob/kgob-schools/KgobSchoolsListView.vue')
            },
            {
              path: 'edit/:id',
              name: 'kgob-schools-edit',
              component: () => import('@/views/kgob/kgob-schools/KgobSchoolsEditView.vue')
            }
          ]
        },
        {
          path: 'protocols',
          name: 'kgob-protocols',
          component: () => import('@/views/kgob/KgobProtocolsView.vue'),
          meta: { menuKey: 'kgob-protocols' },
          children: [
            {
              path: '',
              name: 'kgob-protocol-list',
              component: () => import('@/views/kgob/kgob-protocols/KgobProtocolListView.vue')
            },
            {
              path: 'edit/:id',
              name: 'kgob-protocol-edit',
              component: () => import('@/views/kgob/kgob-protocols/KgobProtocolEditView.vue')
            }
          ]
        },
        {
          path: 'reviewers',
          name: 'kgob-reviewers',
          component: () => import('@/views/kgob/KgobReviewersView.vue'),
          meta: { menuKey: 'kgob-reviewers' },
          children: [
            {
              path: '',
              name: 'kgob-reviewer-list',
              component: () => import('@/views/kgob/kgob-reviewers/KgobReviewerListView.vue')
            },
            {
              path: 'edit/:id',
              name: 'kgob-reviewer-edit',
              component: () => import('@/views/kgob/kgob-reviewers/KgobReviewerEditView.vue')
            },
            {
              path: 'create',
              name: 'kgob-reviewer-create',
              component: () => import('@/views/kgob/kgob-reviewers/KgobReviewerEditView.vue')
            }
          ]
        }
        // Panel terminarza konkursu tymczasowo wyłączony
        // {
        //   path: 'competitions',
        //   name: 'kgob-competitions',
        //   component: () => import('@/views/kgob/KgobCompetitionsView.vue'),
        //   meta: { menuKey: 'kgob-competitions' }
        // }
      ]
    },

    /**
     * KOOB routings
     */
    {
      path: '/koob',
      name: 'koob',
      component: () => import('@/views/KoobView.vue'),
      meta: { user: 'koob' },
      children: [
        {
          path: '',
          name: 'koob-home',
          component: () => import('@/views/koob/KoobHomeView.vue'),
          meta: { menuKey: 'koob-home' }
        },
        {
          path: 'students',
          name: 'koob-students',
          component: () => import('@/views/koob/KoobStudentsView.vue'),
          meta: { menuKey: 'koob-students' },
          children: [
            {
              path: '',
              name: 'koob-students-list',
              component: () => import('@/views/koob/koob-students/KoobStudentListView.vue')
            }
          ]
        },
        {
          path: 'schools',
          name: 'koob-schools',
          component: () => import('@/views/koob/KoobSchoolsView.vue'),
          meta: { menuKey: 'koob-schools' },
          children: [
            {
              path: '',
              name: 'koob-schools-list',
              component: () => import('@/views/koob/koob-schools/KoobSchoolListView.vue')
            }
          ]
        }
      ]
    },

    /**
     * REVIEWER routings
     */
    {
      path: '/reviewer',
      name: 'reviever',
      component: () => import('@/views/ReviewerView.vue'),
      meta: { user: 'reviewer' },
      children: [
        {
          path: '',
          name: 'reviewer-home',
          component: () => import('@/views/reviewer/ReviewerHomeView.vue'),
          meta: { menuKey: 'reviewer-home' }
        },
        {
          path: 'students',
          name: 'reviewer-students',
          component: () => import('@/views/reviewer/ReviewerStudentsView.vue'),
          meta: { menuKey: 'reviewer-students' },
          children: [
            {
              path: '',
              name: 'reviewer-students-list',
              component: () =>
                import('@/views/reviewer/reviewer-students/ReviewerStudentsListView.vue')
            },
            {
              path: ':id/edit-review',
              name: 'reviewer-students-edit-review',
              component: () => import('@/views/reviewer/reviewer-review/ReviewerReviewEditView.vue')
            }
          ]
        }
      ]
    },

    /**
     * SCHOOL routings
     */
    {
      path: '/school',
      name: 'school',
      component: () => import('@/views/SchoolView.vue'),
      meta: { user: 'school' },
      children: [
        {
          path: '',
          name: 'school-home',
          component: () => import('@/views/school/SchoolHomeView.vue'),
          meta: { menuKey: 'school-home' }
        },
        // {
        //   path: 'download-test/:token',
        //   name: 'school-downloadtest',
        //   component: () => import('@/views/school/SchoolDownloadTestView.vue')
        // },
        {
          path: 'download-test/',
          name: 'school-downloadtest',
          component: () => import('@/views/school/SchoolDownloadTestView.vue')
        },
        {
          path: 'create-competition-protocol',
          name: 'school-competition-protocol-create',
          component: () => import('@/views/school/SchoolCreateCompetitionProtocolView.vue')
        },
        {
          path: 'view-competition-protocol',
          name: 'school-competition-protocol-show',
          component: () => import('@/views/school/SchoolShowCompetitionProtocolView.vue')
        },
        {
          path: 'students',
          name: 'school-students',
          component: () => import('@/views/school/SchoolStudentsView.vue'),
          meta: { menuKey: 'school-students' },
          children: [
            {
              path: '',
              name: 'school-students-list',
              component: () => import('@/views/school/school-students/SchoolStudentsListView.vue')
            },
            {
              path: 'add',
              name: 'school-students-add',
              component: () => import('@/views/school/school-students/SchoolStudentsCreateView.vue')
            },
            {
              path: 'edit/:id',
              name: 'school-students-edit',
              component: () => import('@/views/school/school-students/SchoolStudentsEditView.vue')
            },
            {
              path: 'work-topic-change/:id',
              name: 'school-students-work-topic-change',
              component: () =>
                import('@/views/school/school-students/SchoolStudentsWorkTopicChange.vue')
            },
            {
              path: 'file_upload/:id',
              name: 'school-students-file-upload',
              component: () =>
                import('@/views/school/school-students/SchoolStudentsFileUploadView.vue')
            }
          ]
        }
      ]
    },

    /**
     * STUDENT routings
     */
    {
      path: '/student',
      name: 'student',
      component: () => import('@/views/StudentView.vue'),
      meta: { user: 'student' },
      children: [
        {
          path: '',
          name: 'student-home',
          component: () => import('@/views/student/StudentHomeView.vue')
        }
      ]
    },

    /**
     * Dev/test only
     */
    ...(import.meta.env.PROD ? [] : [
      {
        path: '/layout-test',
        name: 'layout-test',
        component: () => import('@/views/dev/LayoutTestView.vue'),
        meta: {}
      }
    ]),

    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotfoundView.vue')
    }
  ],

  scrollBehavior: appScrollBehavior
})

/**
 * Możliwe cele przewijania przy zmianie strony / filtra w obrębie listy.
 * Znaczniki są w DOM zawsze — przełącznik decyduje tylko, w który celujemy.
 *
 *   LIST_TOP — pierwsza pozycja listy, filtry zostają nad ekranem
 *              (znacznik w DataPaginator)
 *   PAGE_TOP — szczyt zawartości panelu, razem z filtrami
 *              (znacznik w AppPanel)
 */
export const SCROLL_TARGET = {
  LIST_TOP: '[data-list-top]',
  PAGE_TOP: '[data-page-top]'
}

/**
 * ⇄ PRZEŁĄCZNIK DO TESTÓW Z UŻYTKOWNIKAMI
 *
 * Zmiana tej jednej stałej przestawia zachowanie paginacji we wszystkich listach.
 * Nic więcej nie trzeba ruszać — obie wersje są w pełni działające.
 *
 * LIST_TOP: po kliknięciu paginacji widać od razu kolejne pozycje. Minus —
 *           bywa mylące, bo brak ruchu filtrów utrudnia poznanie, że to początek
 *           nowej strony.
 * PAGE_TOP: klasyczne „na samą górę". Czytelniejszy sygnał zmiany strony,
 *           ale na wąskim ekranie trzeba przewinąć przez filtry do listy.
 */
export const PAGINATION_SCROLL_TARGET = SCROLL_TARGET.LIST_TOP

/**
 * Pozycja scrolla po nawigacji.
 *
 * Bez `scrollBehavior` vue-router NIE zarządza scrollem w ogóle (nie próbuje i nie
 * zawodzi — po prostu się nie miesza), a `history.pushState` z definicji nigdy nie
 * rusza viewportu. Skutkiem była lista, która po zmianie strony zostawała przewinięta
 * na dole, przy paginacji: trasa się nie zmienia (leci tylko query), więc widok nie
 * jest przemontowywany, a wysokość strony po podmianie wierszy jest ta sama.
 *
 * UWAGA — w tej aplikacji NIE wystarczy `return { top: 0 }`. Okno się nie przewija:
 * BasicLayout to `h-screen`, a jego środek `overflow-hidden`, więc window.scrollY
 * stale wynosi 0, a window.scrollTo (którym vue-router realizuje zwracaną pozycję)
 * jest bezczynne. Faktyczny scroll siedzi w `[data-app-scroll]` wewnątrz AppPanel
 * i trzeba go przewinąć ręcznie, zwracając routerowi `false`.
 *
 * Kolejność warunków jest istotna:
 *   1. hash — powrót z widoku edycji na kotwicę (`#student-42`). Scroll oddajemy
 *      `useListAnchor`, bo w momencie nawigacji elementu nie ma jeszcze w DOM
 *      (dane listy dociągają się asynchronicznie). `useListAnchor` używa
 *      scrollIntoView, które radzi sobie z kontenerem przewijania samo.
 *   2. ruch w obrębie tej samej trasy (paginacja, zmiana filtra) — do znacznika
 *      wskazanego przez PAGINATION_SCROLL_TARGET.
 *   3. wejście na trasę z zewnątrz — szczyt kontenera, żeby filtry były widoczne
 *      od razu. Dlatego punkt 2 jest ograniczony do `to.name === from.name`.
 *   4. brak znacznika (np. pusta lista) albo brak kontenera — szczyt kontenera,
 *      a bez AppPanel fallback na okno.
 *
 * Czego to NIE robi: nie przywraca pozycji przy wstecz/dalej. `savedPosition` jest
 * tu bezużyteczne, bo vue-router zapisuje pozycję OKNA, czyli zawsze zero.
 * Przywracanie wymagałoby własnego zapisu scrollTop kontenera per wpis historii.
 */
export function appScrollBehavior(to, from) {
  return resolveScroll(to, from, PAGINATION_SCROLL_TARGET)
}

/**
 * Logika przewijania z jawnie podanym celem — rozdzielona od `appScrollBehavior`,
 * żeby dało się przetestować obie wersje przełącznika. Router woła wariant
 * powyżej, bo jego trzeci argument to `savedPosition`, nie konfiguracja.
 */
export function resolveScroll(to, from, targetSelector) {
  if (to.hash) return false

  const panel = document.querySelector('[data-app-scroll]')
  if (!panel) return { top: 0 }

  const sameRoute = to.name === from?.name
  const target = sameRoute ? panel.querySelector(targetSelector) : null

  if (target) target.scrollIntoView({ block: 'start' })
  else panel.scrollTop = 0

  return false
}

/**
 * Czy wejście na trasę ma zacząć się od wylogowania. Dotyczy tras, na które
 * wchodzi się z linku w mailu — tam sesja aktywna w przeglądarce należy
 * zwykle do kogoś innego niż adresat wiadomości.
 */
export function shouldForceLogout(toMeta, isLoggedIn) {
  return Boolean(toMeta.forceLogout) && isLoggedIn
}

/**
 * Wylogowanie wymuszone przez trasę jest dla użytkownika niespodzianką — nie on
 * je wywołał, tylko link z maila. Bez komunikatu sesja znika bezgłośnie i przy
 * następnym kliknięciu w innej zakładce wygląda to na zerwane połączenie.
 * Kanał `messages` (toast bottom-center), nie `alerts` — to informacja o tym,
 * co system właśnie zrobił, a nie błąd do naprawienia.
 */
export const FORCE_LOGOUT_NOTICE = {
  severity: 'warn',
  summary: 'Wylogowano',
  detail: 'Poprzednia sesja została zamknięta, żeby wykonać operację z linku',
  life: 5000
}

export function routeGuardCheck(toMeta, userRole, isLoggedIn) {
  if (toMeta.user == null || toMeta.user === userRole) return null
  if (!isLoggedIn) return { redirect: 'login', detail: null }
  // Trasa publiczna odwiedzona przez zalogowanego (np. powrót na "/" po otwarciu
  // przeglądarki) to nie błąd — cicho odsyłamy na stronę główną roli. Prawdziwe
  // wymuszenie wylogowania (link z maila) ma własny, przyjazny komunikat:
  // shouldForceLogout + FORCE_LOGOUT_NOTICE, obsłużone osobno w router.beforeEach.
  const detail = toMeta.user === 'public'
    ? null
    : 'Adres niedostępny dla użytkownika'
  return { redirect: `${userRole}-home`, detail }
}

router.beforeEach(async (to, from) => {
  const userStore = useUserStore()
  const notifications = useNotificationStore()
  await userStore.restoreSession()

  // Przed routeGuardCheck: handleLogout() ustawia userRole na 'public',
  // więc trasa oznaczona meta.user === 'public' przepuści już bez odbicia.
  if (shouldForceLogout(to.meta, userStore.isLoggedIn)) {
    await userStore.handleLogout()
    // Kopia, bo NotificationPoint dopisuje `group` do przekazanego obiektu.
    notifications.addMessage({ ...FORCE_LOGOUT_NOTICE })
  }

  const check = routeGuardCheck(to.meta, userStore.userRole, userStore.isLoggedIn)
  if (check) {
    if (check.detail) notifications.addAlert({ severity: 'error', summary: 'Błąd', detail: check.detail, life: 3000 })
    return { name: check.redirect }
  }

  const loader = useLoaderStore()
  if (
    to.meta.loadergroup == null ||
    from.meta.loadergroup == null ||
    to.meta.loadergroup !== from.meta.loadergroup
  ) {
    loader.reset()
  }
})

export default router
